import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { LayoutGrid, ChevronRight } from "lucide-react";
import { trodoAnalyticsTheme as theme } from "features/analytics/studio/theme";
import { useTeam } from "shared/context/teamContext";
import { fetchBoards, fetchCards } from "features/analytics/studio/api/analyticsApi";

const BORDER = theme.border;

/**
 * Home widget: browse boards (reports) and cards with links into the analytics studio.
 */
export default function QuickLinksCard() {
  const { team } = useParams();
  const navigate = useNavigate();
  const { selectedTeam } = useTeam();
  const teamId = selectedTeam?.id ?? selectedTeam?._id ?? null;

  const [boards, setBoards] = useState([]);
  const [cardsByBoardId, setCardsByBoardId] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!teamId) {
      setLoading(false);
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const res = await fetchBoards({ teamId });
      const list = res?.data ?? res?.boards ?? [];
      const arr = Array.isArray(list) ? list : [];
      setBoards(arr);
      const next = {};
      await Promise.all(
        arr.map(async (b) => {
          const bid = parseInt(String(b.id), 10);
          if (Number.isNaN(bid) || bid < 1) return;
          try {
            const cr = await fetchCards(bid, teamId);
            const cards = cr?.data ?? cr?.cards ?? [];
            next[bid] = Array.isArray(cards) ? cards : [];
          } catch {
            next[bid] = [];
          }
        })
      );
      setCardsByBoardId(next);
    } catch (e) {
      setError(e?.message || "Could not load reports");
      setBoards([]);
      setCardsByBoardId({});
    } finally {
      setLoading(false);
    }
  }, [teamId]);

  useEffect(() => {
    void load();
  }, [load]);

  const goBoard = (boardId) => {
    if (!team) return;
    navigate(`/${team}/analytics/boards/${boardId}`);
  };

  return (
    <div
      className="flex flex-col h-full min-h-0 rounded-xl border overflow-hidden"
      style={{ borderColor: BORDER, backgroundColor: theme.bgRaised }}
    >
      <div
        className="shrink-0 px-3 py-2.5 border-b flex items-center gap-2"
        style={{ borderColor: BORDER, backgroundColor: theme.bgOverlay }}
      >
        <span
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg"
          style={{ backgroundColor: `${theme.primaryLight}18`, color: theme.primary }}
        >
          <LayoutGrid size={16} strokeWidth={2.2} />
        </span>
        <div className="min-w-0">
          <div className="text-sm font-semibold" style={{ color: theme.textPrimary }}>
            Reports & cards
          </div>
          <p className="text-[11px] leading-snug" style={{ color: theme.textSecondary }}>
            Open a board or jump to a card
          </p>
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto px-2 py-2">
        {loading && (
          <p className="text-xs px-2 py-3" style={{ color: theme.textMuted }}>
            Loading…
          </p>
        )}
        {error && (
          <p className="text-xs px-2 py-2" style={{ color: "var(--feedback-error)" }}>
            {error}
          </p>
        )}
        {!loading &&
          !error &&
          boards.map((b) => {
            const bid = parseInt(String(b.id), 10);
            if (Number.isNaN(bid) || bid < 1) return null;
            const cards = cardsByBoardId[bid] || [];
            return (
              <div key={bid} className="mb-3 last:mb-0">
                <button
                  type="button"
                  className="w-full flex items-center gap-1 text-left rounded-lg px-2 py-2 text-sm font-medium transition-colors"
                  style={{ color: theme.textPrimary }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = theme.bgOverlay;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                  }}
                  onClick={() => goBoard(bid)}
                >
                  <ChevronRight className="w-4 h-4 shrink-0 opacity-50" />
                  <span className="truncate flex-1">{b.name || "Untitled board"}</span>
                </button>
                {cards.length > 0 && (
                  <ul className="pl-7 pr-1 space-y-0.5 mt-0.5">
                    {cards.map((c) => (
                      <li key={c.id}>
                        <button
                          type="button"
                          className="w-full text-left truncate rounded-md px-2 py-1.5 text-xs transition-colors"
                          style={{ color: theme.textSecondary }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = theme.bgOverlay;
                            e.currentTarget.style.color = theme.textPrimary;
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = "transparent";
                            e.currentTarget.style.color = theme.textSecondary;
                          }}
                          onClick={() => goBoard(bid)}
                        >
                          {c.title || "Untitled card"}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        {!loading && !error && boards.length === 0 && (
          <p className="text-xs px-2 py-3 text-center" style={{ color: theme.textMuted }}>
            No boards yet. Create one in Analytics.
          </p>
        )}
      </div>
    </div>
  );
}
